import {Injectable} from '@angular/core';
import {DateTime, Duration, DurationUnits} from "luxon";
import {formatDate} from "@angular/common";
import {SterilizedInstrument} from "../../features/sterilization/sterilization.types";
@Injectable({ providedIn: 'root' })
export class DateTimeService {

    /**
     * ConvertDateTimeTOString
     * @param dateTime - date in locale format
     */
    public getDateTimeStringFromDateTimeObject(dateTime:DateTime): string{
        return  dateTime.toFormat('yyyy-MM-dd')+"T"+dateTime.toFormat('HH:mm:ss'); //"2024-08-23T08:47:24.858Z"
    }

    /**
     * ConvertDateTOString
     * @param date - date in locale format
     */
    public getDateTimeStringFromDate(date:Date): string{
        return  this.getDateTimeStringFromDateTimeObject(this.dateToDateTime(date))
    }

    public getUtcDateTime(localeDateTime:DateTime): DateTime {
        localeDateTime.setZone(Intl.DateTimeFormat().resolvedOptions().timeZone);
        return localeDateTime.toUTC();
    }

    /**
     * First will set locales from browser, then it converts date to UTC, then to string format required by BE
     * @param localeDate - date in locale format
     */
    public getDateTimeStringFromDateTimeObjectInBEFormat(localeDate:DateTime): string{
        const utcDate = this.getUtcDateTime(localeDate)
        return  utcDate.toFormat('yyyy-MM-dd')+"T"+utcDate.toFormat('HH:mm:ss'); //"2024-08-23T08:47:24.858Z"
    }

    /**
     * First will set locales from browser, then it converts date to UTC, then to string format required by BE
     * @param date - date in locale format
     */
    public getDateTimeStringFromDateObjectInBEFormat(date:Date): string{
        if(date == null) {
            return null;
        }
        return this.getDateTimeStringFromDateTimeObjectInBEFormat(this.dateToDateTime(date));
    }

    /**
     * First will set locales from browser, then it converts date to UTC, then to string format required by BE
     * @param localeDate - date in locale format
     */
    public getDateStringFromDateTimeObjectInBEFormat(localeDate:DateTime): string{
        if(localeDate == null) {
            return null;
        }
        const utcDate = this.getUtcDateTime(localeDate)
        return  utcDate.toFormat('yyyy-MM-dd');
    }

    /**
     * First will set locales from browser, then it converts date to UTC, then to string format required by BE
     * @param date - date in locale format
     */
    public getDateStringFromDateObjectInBEFormat(date:Date): string{
        if(date == null){
            return null;
        }
        return formatDate(date, 'yyyy-MM-dd', 'ro-RO');
    }

    /**
     * First will convert localeDate to UTC, then to string format required by BE
     * @param localeDate - date in locale format
     */
    public getDateAndTimeFromBEInDateObject(utcBEDate): Date{
        if(utcBEDate == null || utcBEDate == ""){
            return null;
        }
        if(utcBEDate instanceof Date){
            return utcBEDate;
        }
        //convert array
        if(Object.prototype.toString.call(utcBEDate) === '[object Array]'){
            if(utcBEDate.length > 3){
                return new Date(Date.UTC(+utcBEDate[0],+utcBEDate[1]-1, +utcBEDate[2], +utcBEDate[3], +utcBEDate[4]));
            }else{
                return new Date(+utcBEDate[0],+utcBEDate[1]-1, +utcBEDate[2]);
            }
        }
        //convert string
        if(typeof utcBEDate === "string"){
            const date:Date = new Date(utcBEDate+"");
            return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
        }
        return null;
    }

    /**
     * convert Date to DateTime
     * @param date
     */
    public dateToDateTime(date:Date):DateTime{
        if (!date) {
            return null;
        }
        return DateTime.fromJSDate(date);
    }

    /**
     * convert DateTime to Date
     * @param date
     */
    public dateTimeToDate(dateTime:DateTime):Date{
        // Get the milliseconds since epoch from the DateTime object
        const millisecondsSinceEpoch = dateTime.toMillis();

        // Create a new Date object using the milliseconds since epoch
        const date = new Date(millisecondsSinceEpoch);

        return date;
    }

    public getNowString():string {
        let options: Intl.DateTimeFormatOptions = {
            day: "numeric", month: "short", year: "numeric",
            hour: "2-digit", minute: "2-digit"
        };
        return new Date().toLocaleDateString("ro-RO", options);
    }

    public getFormatedDate(date:Date):string {
        let options: Intl.DateTimeFormatOptions = {
            day: "numeric", month: "short", year: "numeric",
            hour: "2-digit", minute: "2-digit"
        };
        return date.toLocaleDateString("ro-RO", options);
    }

    public getDateAndTimeOfSterilization(sterilizedBag:SterilizedInstrument) {
        if(sterilizedBag?.timeOfSterilization && sterilizedBag?.timeOfSterilization instanceof Array){
            const date = this.getDateAndTimeFromBEInDateObject(sterilizedBag.timeOfSterilization).toISOString();
            return formatDate(date, 'dd MMM yyyy HH:mm', 'ro-RO');
        }

        if(sterilizedBag?.timeOfSterilization){
            return sterilizedBag?.timeOfSterilization
        }

        return "";
    }

    public getExpirationDateAndTime(sterilizedBag:SterilizedInstrument) {
        if(sterilizedBag?.expirationDate && sterilizedBag?.expirationDate instanceof Array){
            const date = this.getDateAndTimeFromBEInDateObject(sterilizedBag.expirationDate);
            return formatDate(date, 'dd MMM yyyy HH:mm', 'ro-RO');
        }

        if(sterilizedBag?.expirationDate){
            return sterilizedBag?.expirationDate
        }
        return "";
    }

    public dateInPast(date:Date): boolean {
        if(date == null) {
            return true;
        }
        return new Date() > date;
    }

    public getDurationBetweenDatesWithFieldsSpecified(start:Date, end:Date, fields:DurationUnits): Duration {
        const startDateObj = start.getTime() > end.getTime() ? end : start;
        const endDateObj = end.getTime() < start.getTime() ? start : end;

        const startDate:DateTime = this.dateToDateTime(startDateObj);
        const endDate:DateTime = this.dateToDateTime(endDateObj);

        return endDate.diff(startDate, fields);
    }

    public getDurationBetweenDates(start:Date, end:Date): Duration {
        return this.getDurationBetweenDatesWithFieldsSpecified(start,end,["years", "months", "days", "hours"]);
    }

    public getDurationAsStringBetweenDates(startDate:Date, endDate:Date): string {
        const diff = this.getDurationBetweenDates(startDate, endDate);
        let str:string = "";
        let more:boolean = false;
        if(diff?.years > 0){
            more= true;
            const ani = diff.years == 1 ? "an" : "ani";
            str += Math.floor(diff.years) + ` ${ani}, `;
        }
        if(diff?.months > 0){
            const luni = diff.months == 1 ? "luna" : "luni";
            if(more && diff.days < 1){
                str += " si  "+Math.floor(diff.months) + " "+luni;
            }else{
                str += diff.months + ` ${luni}, `
            }
            more = true;
        }
        if(diff?.days > 0){
            const zi = diff.days == 1 ? "zi" : "zile";
            if(more){
                str += " si  "+Math.floor(diff.days) + " "+zi;
            }else {
                str += diff.days + " "+zi;
            }
        }
        return str;
    }


    public getHoursAndMinutesBetweenDates(startDate:Date, endDate:Date): string {
        const diff = this.getDurationBetweenDatesWithFieldsSpecified(startDate,endDate,["hours", "minutes"]);
        let str:string = "";


        if(diff?.hours > 0) {
            const ora = diff.hours == 1 ? "ora" : "ore";
            str += Math.floor(diff.hours) + " "+ora;
        }
        if(diff?.minutes > 0) {
            str += " si " + Math.floor(diff.minutes) + " minute";
        }
        return str;
    }

    /**
     * Human readable duration, using only the units that matter: "1 an, 2 luni și 3 zile",
     * "2 luni și 3 zile", "3 ore și 5 min", "3 min" or "câteva secunde".
     */
    public getReadableDurationBetweenDates(startDate:Date, endDate:Date): string {
        const diff = this.getDurationBetweenDatesWithFieldsSpecified(startDate, endDate, ["years", "months", "days", "hours", "minutes"]);

        let parts:string[] = [
            this.getCountLabel(diff.years, "an", "ani"),
            this.getCountLabel(diff.months, "lună", "luni"),
            this.getCountLabel(diff.days, "zi", "zile")
        ].filter(part => !!part);

        if(parts.length === 0){
            const minutes = Math.floor(diff.minutes);
            parts = [
                this.getCountLabel(diff.hours, "oră", "ore"),
                minutes > 0 ? minutes + " min" : ""
            ].filter(part => !!part);
        }

        if(parts.length === 0){
            return "câteva secunde";
        }
        return parts.length === 1 ? parts[0] : parts.slice(0, -1).join(", ") + " și " + parts[parts.length - 1];
    }

    private getCountLabel(value:number, singular:string, plural:string): string {
        const count = Math.floor(value);
        if(count <= 0){
            return "";
        }
        if(count === 1){
            return count + " " + singular;
        }
        //in Romanian, numbers from 20 up need "de": "3 zile", but "20 de zile"
        const needsDe = count % 100 === 0 || count % 100 >= 20;
        return count + (needsDe ? " de " : " ") + plural;
    }

}
